import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import Layout from '../../components/layout/AppLayout';
import Header from '../../../shared/components/layout/Header';
import { useSidebar } from '../../../shared/contexts/SidebarContext';
import { useSmartBack } from '../../../shared/navigation';
import TimeOnSystemOverview from './TimeOnSystemOverview';
import DeviceBreakdown from './DeviceBreakdown';
import FeatureUsagePanel, { UserFeatureList } from './FeatureUsagePanel';
import InteractionsPanel, { UserInteractionSummary } from './InteractionsPanel';
import {
  downloadUserActivityCsv,
  downloadUserActivityPdf,
  getUserActivityByBuilder,
  getUserActivityOverview,
  getUserActivityByUser,
  getUserActivityDay,
  getUserActivityDevices,
  getUserActivitySessions,
  getUserActivityFeatures,
  getUserActivityInteractions,
  getUserActivityUserFeatures,
  getUserActivityUserInteractions,
  getUserActivitySummary
} from '../../services/api/implementation/impl';
import {
  buildDayStrip,
  daysAgoIst,
  daysInRange,
  describeCoverageGap,
  formatClockIst,
  formatDateTimeIst,
  formatDayIst,
  formatWorkedDuration,
  groupUsersByCompany,
  partitionStaff,
  summariseUsers,
  todayIst,
  toHours,
  validateCustomRange
} from '../../utils/timeOnSystem';

const RANGE_PRESETS = [
  { key: 'today', label: 'Today', days: 0 },
  { key: '7d', label: 'Last 7 days', days: 6 },
  { key: '30d', label: 'Last 30 days', days: 29 },
  // Custom takes its dates from the inputs; see appliedCustom.
  { key: 'custom', label: 'Custom', days: null }
];

function SummaryTile({ label, value, sub, tone = 'neutral' }) {
  const valueTone = tone === 'warn' ? 'text-orange-700' : 'text-gray-900';
  return (
    <div className="flex-1 min-w-[170px] rounded-lg border border-gray-200 bg-white px-4 py-3">
      <div className="text-[11px] uppercase tracking-wider text-gray-500">{label}</div>
      <div className={`mt-1 text-2xl font-semibold tabular-nums ${valueTone}`}>{value}</div>
      {sub ? <div className="mt-0.5 text-xs text-gray-500">{sub}</div> : null}
    </div>
  );
}

/**
 * One person in detail: the slots behind their day, and how they signed in.
 *
 * Makes a number answerable instead of only assertable — the gaps behind a
 * total, and the devices behind a login count, are visible rather than
 * something the reader has to take on trust.
 */
function UserDetailModal({ user, range, onClose }) {
  const singleDay = range.from === range.to;
  const day = range.to;
  const { data, isLoading, isError } = useQuery({
    queryKey: ['userActivityDay', user?.user_id, day],
    queryFn: () => getUserActivityDay(user.user_id, day),
    // The slot strip only means something for a single day; across a range the
    // sign-in history stands on its own.
    enabled: Boolean(user?.user_id) && singleDay
  });
  const sessionsQuery = useQuery({
    queryKey: ['userActivitySessions', user?.user_id, range.from, range.to],
    queryFn: () => getUserActivitySessions(user.user_id, range),
    enabled: Boolean(user?.user_id)
  });
  const sessions = sessionsQuery.data?.sessions || [];
  const userFeaturesQuery = useQuery({
    queryKey: ['userActivityUserFeatures', user?.user_id, range.from, range.to],
    queryFn: () => getUserActivityUserFeatures(user.user_id, range),
    enabled: Boolean(user?.user_id)
  });
  const userInteractionsQuery = useQuery({
    queryKey: ['userActivityUserInteractions', user?.user_id, range.from, range.to],
    queryFn: () => getUserActivityUserInteractions(user.user_id, range),
    enabled: Boolean(user?.user_id)
  });

  const slots = useMemo(
    () =>
      buildDayStrip(data?.buckets, {
        day: data?.day || day,
        bucketMinutes: data?.bucket_minutes || 5
      }),
    [data, day]
  );

  const activeSlots = slots.filter((s) => s.active).length;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 px-4"
      onClick={onClose}>
      <div
        className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-lg bg-white p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-lg font-semibold text-gray-900">
              {user?.name || `User #${user?.user_id}`}
            </div>
            <div className="text-sm text-gray-500">
              {singleDay ? day : `${range.from} → ${range.to}`} ·{' '}
              {user?.designation || user?.vertical || 'No role set'}
              {user?.company_name ? ` · ${user.company_name}` : ''}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded px-2 py-1 text-sm text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary">
            Close
          </button>
        </div>

        {!singleDay ? null : isLoading ? (
          <div className="py-10 text-center text-sm text-gray-500">Loading activity…</div>
        ) : isError ? (
          <div className="py-10 text-center text-sm text-red-600">
            Could not load this day. Try again in a moment.
          </div>
        ) : (
          <>
            <div className="mt-5 flex flex-wrap gap-1">
              {slots.map((slot) => (
                <div
                  key={slot.startMs}
                  title={`${slot.label} — ${
                    slot.active ? `${slot.requests} request(s)` : 'no activity'
                  }`}
                  className={`h-5 w-2.5 rounded-sm ${
                    slot.active ? 'bg-primary' : 'bg-gray-200'
                  }`}
                />
              ))}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-gray-500">
              <span>06:00 → 23:00 IST</span>
              <span>
                {activeSlots} slot{activeSlots === 1 ? '' : 's'} with activity ·{' '}
                {data?.bucket_minutes || 5} min each
              </span>
            </div>
            <p className="mt-4 border-t border-gray-100 pt-3 text-xs leading-relaxed text-gray-500">
              Filled blocks are slots this person used Housingram in. Gaps mean the
              product was not in use — away, in a meeting, or working elsewhere.
              Background polling is excluded, so an open tab alone never fills a
              block.
            </p>
          </>
        )}

        <div className={singleDay ? 'mt-5 border-t border-gray-100 pt-4' : 'mt-5'}>
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h3 className="text-sm font-semibold text-gray-900">Sign-ins</h3>
            <span className="text-xs text-gray-500">
              {sessions.length} in use during this period
            </span>
          </div>
          {sessionsQuery.isLoading ? (
            <div className="py-6 text-center text-sm text-gray-500">Loading sign-ins…</div>
          ) : sessionsQuery.isError ? (
            <div className="py-6 text-center text-sm text-red-600">
              Could not load sign-ins. Try again in a moment.
            </div>
          ) : !sessions.length ? (
            <div className="py-6 text-center text-sm text-gray-500">
              No sign-ins were in use during this period.
            </div>
          ) : (
            <div className="mt-2 max-h-64 overflow-y-auto rounded-md border border-gray-100">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500">
                    <th className="px-3 py-2 text-left font-medium">Device</th>
                    <th className="px-3 py-2 text-right font-medium">Signed in</th>
                    <th className="px-3 py-2 text-right font-medium">Last active</th>
                  </tr>
                </thead>
                <tbody>
                  {sessions.map((s, i) => (
                    <tr
                      key={`${s.created_at}-${i}`}
                      className={`border-t border-gray-100 ${
                        s.automated ? 'text-gray-400' : 'text-gray-900'
                      }`}>
                      <td className="px-3 py-2">
                        {s.label}
                        {s.automated ? (
                          <span className="ml-2 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-gray-500">
                            not a person
                          </span>
                        ) : null}
                        {s.signed_out ? (
                          <span className="ml-2 text-xs text-gray-400">signed out</span>
                        ) : null}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {formatDateTimeIst(s.created_at)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-500">
                        {formatDateTimeIst(s.last_seen_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="mt-2 text-xs text-gray-400">
            &ldquo;Last active&rdquo; can lag on the mobile app, which only reports in
            when it refreshes its sign-in.
          </p>
        </div>

        <UserFeatureList
          data={userFeaturesQuery.data}
          isLoading={userFeaturesQuery.isLoading}
          isError={userFeaturesQuery.isError}
        />
        <UserInteractionSummary
          data={userInteractionsQuery.data}
          isLoading={userInteractionsQuery.isLoading}
          isError={userInteractionsQuery.isError}
        />
      </div>
    </div>
  );
}

/** Time cell with a proportional bar, so a column of numbers is scannable. */
function TimeCell({ minutes, max }) {
  if (!minutes || minutes <= 0) {
    return <span className="text-gray-400">—</span>;
  }
  const pct = max > 0 ? Math.max(4, Math.round((minutes / max) * 100)) : 0;
  return (
    <div className="inline-flex w-full max-w-[120px] flex-col items-end gap-1">
      <span className="tabular-nums text-gray-900">{formatWorkedDuration(minutes)}</span>
      <span className="h-1 w-full rounded-full bg-gray-100">
        <span
          className="block h-1 rounded-full bg-primary"
          style={{ width: `${pct}%` }}
        />
      </span>
    </div>
  );
}

const TimeOnSystem = () => {
  const { collapsed } = useSidebar();
  const { onBack } = useSmartBack({ fallback: '/builder/dashboard' });

  const [preset, setPreset] = useState('today');
  const [builderId, setBuilderId] = useState(null);
  const [drillUser, setDrillUser] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [view, setView] = useState('platform');
  const [hideInactive, setHideInactive] = useState(false);
  const [showNonStaff, setShowNonStaff] = useState(false);
  // Draft dates live in the inputs; the page only refetches once they are
  // applied, so half-typed dates never hit the server.
  const [customFrom, setCustomFrom] = useState(() => daysAgoIst(6));
  const [customTo, setCustomTo] = useState(() => todayIst());
  const [appliedCustom, setAppliedCustom] = useState(null);

  const customError = validateCustomRange(customFrom, customTo);

  const range = useMemo(() => {
    if (preset === 'custom' && appliedCustom) return appliedCustom;
    const days = RANGE_PRESETS.find((p) => p.key === preset)?.days ?? 0;
    return { from: daysAgoIst(days), to: todayIst() };
  }, [preset, appliedCustom]);

  const selectPreset = (key) => {
    setPreset(key);
    // Custom opens on the dates already in the inputs, so choosing it shows
    // data straight away instead of an empty page waiting for Apply.
    if (key === 'custom' && !validateCustomRange(customFrom, customTo)) {
      setAppliedCustom({ from: customFrom, to: customTo });
    }
  };

  const applyCustom = () => {
    if (validateCustomRange(customFrom, customTo)) return;
    setAppliedCustom({ from: customFrom, to: customTo });
  };

  /**
   * Doubles as the platform-admin probe: the endpoint 403s for a builder's own
   * management user, so a failure here means "not platform staff" rather than a
   * problem worth surfacing. That avoids guessing at roles on the client.
   */
  const buildersQuery = useQuery({
    queryKey: ['userActivityBuilders', range.from, range.to],
    queryFn: () => getUserActivityByBuilder(range),
    retry: false
  });

  const isPlatformAdmin = buildersQuery.isSuccess;
  const builders = buildersQuery.data?.builders || [];
  const effectiveBuilderId = isPlatformAdmin ? builderId ?? builders[0]?.builder_id : null;

  // Cross-builder accounts land on the platform view; everyone else only ever
  // has one builder, so the toggle would be a control with nothing behind it.
  const showPlatformView = isPlatformAdmin && view === 'platform';

  const overviewQuery = useQuery({
    queryKey: ['userActivityOverview', range.from, range.to],
    queryFn: () => getUserActivityOverview(range),
    enabled: showPlatformView
  });

  const summaryQuery = useQuery({
    queryKey: ['userActivitySummary', range.from, range.to, effectiveBuilderId],
    queryFn: () => getUserActivitySummary({ ...range, builderId: effectiveBuilderId }),
    enabled: !buildersQuery.isLoading && !showPlatformView
  });

  const usersQuery = useQuery({
    queryKey: ['userActivityUsers', range.from, range.to, effectiveBuilderId],
    queryFn: () => getUserActivityByUser({ ...range, builderId: effectiveBuilderId }),
    enabled:
      !buildersQuery.isLoading &&
      !showPlatformView &&
      (!isPlatformAdmin || Boolean(effectiveBuilderId))
  });

  // Sign-ins and devices follow whichever view is open: the whole platform in
  // "All builders", the selected builder in "By user".
  const devicesBuilderId = showPlatformView ? undefined : effectiveBuilderId;
  const devicesQuery = useQuery({
    queryKey: ['userActivityDevices', range.from, range.to, devicesBuilderId ?? 'all'],
    queryFn: () => getUserActivityDevices({ ...range, builderId: devicesBuilderId }),
    enabled:
      !buildersQuery.isLoading &&
      (showPlatformView || !isPlatformAdmin || Boolean(effectiveBuilderId))
  });
  // Features and page views follow the same scope as sign-ins and devices.
  const featuresQuery = useQuery({
    queryKey: ['userActivityFeatures', range.from, range.to, devicesBuilderId ?? 'all'],
    queryFn: () => getUserActivityFeatures({ ...range, builderId: devicesBuilderId }),
    enabled:
      !buildersQuery.isLoading &&
      (showPlatformView || !isPlatformAdmin || Boolean(effectiveBuilderId))
  });
  const interactionsQuery = useQuery({
    queryKey: ['userActivityInteractions', range.from, range.to, devicesBuilderId ?? 'all'],
    queryFn: () => getUserActivityInteractions({ ...range, builderId: devicesBuilderId }),
    enabled:
      !buildersQuery.isLoading &&
      (showPlatformView || !isPlatformAdmin || Boolean(effectiveBuilderId))
  });

  const allRows = usersQuery.data?.users || [];
  // Leads and other CRM records share the user table with staff, and nothing
  // else in the payload distinguishes them — a lead reads as a Management
  // Admin. Drop them unless asked for, and say how many were dropped rather
  // than quietly changing the count.
  const { staff, nonStaff } = useMemo(() => partitionStaff(allRows), [allRows]);
  const users = showNonStaff ? allRows : staff;

  // Totals come from the rows on screen, not from the summary endpoint, so the
  // tiles cannot disagree with the table underneath them.
  const totals = useMemo(() => summariseUsers(users), [users]);
  const activeCount = totals.activeUsers;
  const method = summaryQuery.data?.method;
  const isSingleDay = range.from === range.to;
  const trackingOff = method && method.tracking_enabled === false;
  const coverageGap = useMemo(
    () => describeCoverageGap(range, summaryQuery.data?.coverage),
    [range, summaryQuery.data]
  );

  const groups = useMemo(
    () => groupUsersByCompany(users, { hideInactive }),
    [users, hideInactive]
  );
  const maxMinutes = useMemo(
    () => users.reduce((m, u) => Math.max(m, u.active_minutes || 0), 0),
    [users]
  );

  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await downloadUserActivityCsv({
        ...range,
        builderId: effectiveBuilderId
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `time-on-system-${range.from}-to-${range.to}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('CSV export failed', err);
    } finally {
      setExporting(false);
    }
  };

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const blob = await downloadUserActivityPdf({
        ...range,
        // The platform view reports every builder; the user view reports the
        // one being looked at, so the document matches what is on screen.
        builderId: showPlatformView ? undefined : effectiveBuilderId
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `housingram-usage-${range.from}-to-${range.to}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF download failed', err);
    } finally {
      setDownloadingPdf(false);
    }
  };

  const colSpan = isSingleDay ? 10 : 11;

  return (
    <Layout>
      <div
        className={`fixed z-[10] top-0 transition-all duration-300 ease-in-out ${
          collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
        }`}>
        <Header data={{ name: 'Time on system' }} backArrow={true} onBack={onBack} />
      </div>

      <div className="mt-[4.5rem] px-4 pb-10">
        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 py-3">
          {isPlatformAdmin ? (
            <div className="flex items-center gap-1 rounded-md border border-gray-200 p-0.5">
              {[
                { key: 'platform', label: 'All builders' },
                { key: 'users', label: 'By user' }
              ].map((option) => (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => setView(option.key)}
                  className={`rounded px-3 py-1 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-primary ${
                    view === option.key
                      ? 'bg-gray-900 text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}>
                  {option.label}
                </button>
              ))}
            </div>
          ) : null}

          <div className="flex items-center gap-1">
            {RANGE_PRESETS.map((option) => (
              <button
                key={option.key}
                type="button"
                onClick={() => selectPreset(option.key)}
                className={`rounded-md px-3 py-1 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-primary ${
                  preset === option.key
                    ? 'bg-primary text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}>
                {option.label}
              </button>
            ))}
          </div>

          {preset === 'custom' ? (
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="date"
                aria-label="Start date"
                value={customFrom}
                max={customTo || todayIst()}
                onChange={(e) => setCustomFrom(e.target.value)}
                className="rounded-md border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <span className="text-xs text-gray-500">to</span>
              <input
                type="date"
                aria-label="End date"
                value={customTo}
                min={customFrom}
                max={todayIst()}
                onChange={(e) => setCustomTo(e.target.value)}
                className="rounded-md border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <button
                type="button"
                onClick={applyCustom}
                disabled={Boolean(customError)}
                className="rounded-md bg-primary px-3 py-1 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-primary">
                Apply
              </button>
              {customError ? (
                <span className="text-xs text-orange-700">{customError}</span>
              ) : null}
            </div>
          ) : null}

          {isPlatformAdmin && !showPlatformView && builders.length > 0 ? (
            <select
              value={effectiveBuilderId ?? ''}
              onChange={(e) => setBuilderId(Number(e.target.value))}
              className="rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary">
              {builders.map((b) => (
                <option key={b.builder_id} value={b.builder_id}>
                  {b.builder_name || `Builder #${b.builder_id}`} · {b.active_users} active
                </option>
              ))}
            </select>
          ) : null}

          {!showPlatformView ? (
          <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={hideInactive}
              onChange={(e) => setHideInactive(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 accent-[#1c5f4c]"
            />
            Only users with activity
          </label>
          ) : null}

          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs text-gray-500">
              {range.from}
              {isSingleDay
                ? ''
                : ` → ${range.to} · ${daysInRange(range.from, range.to)} days`}
            </span>
            <button
              type="button"
              onClick={handleDownloadPdf}
              disabled={downloadingPdf}
              className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-primary">
              {downloadingPdf ? 'Building PDF…' : 'Download PDF'}
            </button>
            <button
              type="button"
              onClick={handleExport}
              disabled={exporting || !users.length || showPlatformView}
              className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-primary">
              {exporting ? 'Preparing…' : 'Export CSV'}
            </button>
          </div>
        </div>

        {/* Tracking is off — the single most useful thing the page can say when
            every row is a dash, because zeroes otherwise look like a broken
            feature rather than a switch nobody has flipped. */}
        {trackingOff ? (
          <div className="mb-4 flex gap-3 rounded-lg border-l-4 border-orange-500 bg-orange-50 px-4 py-3">
            <div>
              <div className="text-sm font-semibold text-orange-900">
                Activity tracking is not switched on for this environment
              </div>
              <p className="mt-1 text-sm leading-relaxed text-orange-800">
                Every user reads zero because nothing is being recorded yet, not
                because nobody logged in. Once{' '}
                <code className="rounded bg-orange-100 px-1">
                  USER_ACTIVITY_TRACKING_ENABLED
                </code>{' '}
                is set, numbers build up from that moment — earlier activity cannot
                be backfilled.
              </p>
            </div>
          </div>
        ) : null}

        {/* Part of the range is older than tracking itself. Without this the
            screen shows Logins, Views and Clicks moving with the dates while
            Time and Days do not — which looks like the date filter is broken
            rather than like history that was never recorded. */}
        {!trackingOff && coverageGap ? (
          <div className="mb-4 rounded-lg border-l-4 border-blue-500 bg-blue-50 px-4 py-3">
            <div className="text-sm font-semibold text-blue-900">
              Time on system has only been recorded since{' '}
              {formatDayIst(coverageGap.firstDay)}
            </div>
            <p className="mt-1 text-sm leading-relaxed text-blue-800">
              {coverageGap.coveredDays === 0
                ? `None of the ${coverageGap.totalDays} days in this range have minutes to show.`
                : `${coverageGap.missingDays} of the ${coverageGap.totalDays} days in this range have no minutes to show — only ${coverageGap.coveredDays} do.`}{' '}
              Logins come from the sign-in history and Views and Clicks from
              Mixpanel, both of which go back further, so widening the dates moves
              those columns while Time and Days stay put. Minutes before tracking
              was switched on cannot be backfilled.
            </p>
          </div>
        ) : null}

        {showPlatformView ? (
          <TimeOnSystemOverview
            overview={overviewQuery.data}
            isLoading={overviewQuery.isLoading}
            isError={overviewQuery.isError}
            onSelectBuilder={(id) => {
              setBuilderId(id);
              setView('users');
            }}
          />
        ) : (
          <>
        {/* Summary */}
        <div className="flex flex-wrap gap-3">
          <SummaryTile
            label="Active users"
            value={`${activeCount} / ${users.length}`}
            sub={
              users.length
                ? `${users.length - activeCount} did not open Housingram`
                : null
            }
            tone={users.length && activeCount === 0 ? 'warn' : 'neutral'}
          />
          <SummaryTile
            label="Average time"
            value={formatWorkedDuration(totals.avgMinutes)}
            sub="per active user"
          />
          <SummaryTile
            label="Total time"
            value={`${toHours(totals.totalMinutes)} h`}
            sub={isSingleDay ? 'today' : `across ${summaryQuery.data?.days || 0} days`}
          />
          <SummaryTile
            label="Companies"
            value={groups.length}
            sub={`${groups.filter((g) => g.activeUsers > 0).length} with activity`}
          />
        </div>

        {/* What was filtered out, and how to see it. A silently shorter list is
            worse than a longer one — the reader has no way to know it happened. */}
        {nonStaff.length ? (
          <div className="mt-4 flex flex-wrap items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600">
            <span>
              {showNonStaff ? 'Showing' : 'Hiding'} {nonStaff.length} non-staff record
              {nonStaff.length === 1 ? '' : 's'} — leads and other CRM entries that
              share the user list but cannot sign in.
            </span>
            <button
              type="button"
              onClick={() => setShowNonStaff((v) => !v)}
              className="rounded border border-gray-300 bg-white px-2 py-0.5 font-medium text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary">
              {showNonStaff ? 'Hide them' : 'Show them'}
            </button>
          </div>
        ) : null}

        {/* Users, grouped by company */}
        <div className="mt-5 overflow-x-auto rounded-lg border border-gray-200 bg-white">
          <table
            className={`w-full border-collapse text-sm ${
              isSingleDay ? 'min-w-[980px]' : 'min-w-[1100px]'
            }`}>
            <thead>
              <tr className="bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500">
                <th className="px-4 py-2.5 text-left font-medium">User</th>
                <th className="px-4 py-2.5 text-left font-medium">Role</th>
                <th className="px-4 py-2.5 text-right font-medium">Time</th>
                {!isSingleDay ? (
                  <th className="px-4 py-2.5 text-right font-medium">Days</th>
                ) : null}
                <th className="px-4 py-2.5 text-right font-medium">Logins</th>
                <th className="px-4 py-2.5 text-right font-medium">Devices</th>
                <th className="px-4 py-2.5 text-left font-medium">Top feature</th>
                <th className="px-4 py-2.5 text-right font-medium">Views</th>
                <th className="px-4 py-2.5 text-right font-medium">Clicks</th>
                <th
                  className="px-4 py-2.5 text-right font-medium"
                  title={
                    isSingleDay
                      ? undefined
                      : 'Earliest activity anywhere in the range'
                  }>
                  {isSingleDay ? 'First' : 'First seen'}
                </th>
                <th
                  className="px-4 py-2.5 text-right font-medium"
                  title={
                    isSingleDay ? undefined : 'Latest activity anywhere in the range'
                  }>
                  {isSingleDay ? 'Last' : 'Last seen'}
                </th>
              </tr>
            </thead>
            <tbody>
              {usersQuery.isLoading ? (
                <tr>
                  <td colSpan={colSpan} className="px-4 py-10 text-center text-gray-500">
                    Loading…
                  </td>
                </tr>
              ) : usersQuery.isError ? (
                <tr>
                  <td colSpan={colSpan} className="px-4 py-10 text-center text-red-600">
                    Could not load activity. Try again in a moment.
                  </td>
                </tr>
              ) : !groups.length ? (
                <tr>
                  <td colSpan={colSpan} className="px-4 py-10 text-center text-gray-500">
                    {hideInactive
                      ? 'No user had activity in this range.'
                      : 'No users to show for this selection.'}
                  </td>
                </tr>
              ) : (
                groups.map((group) => (
                  <React.Fragment key={group.key}>
                    <tr className="bg-gray-50/80">
                      <td
                        colSpan={colSpan}
                        className="border-y border-gray-200 px-4 py-2">
                        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                          <span className="text-[11px] font-semibold uppercase tracking-wider text-[#1c5f4c]">
                            {group.companyName}
                          </span>
                          <span className="text-xs text-gray-500">
                            {group.activeUsers} of {group.totalUsers} active
                          </span>
                          {group.totalMinutes > 0 ? (
                            <span className="text-xs text-gray-500">
                              · {formatWorkedDuration(group.totalMinutes)} total
                            </span>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                    {group.users.map((user) => {
                      const idle = user.active_minutes <= 0;
                      return (
                        <tr
                          key={user.user_id}
                          onClick={() => setDrillUser(user)}
                          className="cursor-pointer border-t border-gray-100 hover:bg-gray-50">
                          <td className="px-4 py-2.5 text-gray-900">
                            {user.name || `User #${user.user_id}`}
                          </td>
                          <td className="px-4 py-2.5 text-gray-500">
                            {user.designation || user.vertical || '—'}
                          </td>
                          <td className="px-4 py-2.5 text-right">
                            <TimeCell minutes={user.active_minutes} max={maxMinutes} />
                          </td>
                          {!isSingleDay ? (
                            <td className="px-4 py-2.5 text-right tabular-nums text-gray-500">
                              {user.days_active || '—'}
                            </td>
                          ) : null}
                          <td className="px-4 py-2.5 text-right tabular-nums text-gray-500">
                            {user.logins || '—'}
                          </td>
                          <td
                            className="px-4 py-2.5 text-right tabular-nums text-gray-500"
                            title={(user.device_labels || []).join(', ') || undefined}>
                            {user.device_types || '—'}
                          </td>
                          <td
                            className="max-w-[160px] truncate px-4 py-2.5 text-gray-600"
                            title={user.top_feature || undefined}>
                            {user.top_feature || '—'}
                          </td>
                          <td className="px-4 py-2.5 text-right tabular-nums text-gray-500">
                            {user.page_views || '—'}
                          </td>
                          <td className="px-4 py-2.5 text-right tabular-nums text-gray-500">
                            {user.clicks || '—'}
                          </td>
                          <td
                            className={`px-4 py-2.5 text-right tabular-nums ${
                              idle ? 'text-gray-400' : 'text-gray-500'
                            }`}>
                            {isSingleDay
                              ? formatClockIst(user.first_seen_at)
                              : formatDateTimeIst(user.first_seen_at)}
                          </td>
                          <td
                            className={`px-4 py-2.5 text-right tabular-nums ${
                              idle ? 'text-gray-400' : 'text-gray-500'
                            }`}>
                            {isSingleDay
                              ? formatClockIst(user.last_seen_at)
                              : formatDateTimeIst(user.last_seen_at)}
                          </td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>

          </>
        )}

        <DeviceBreakdown
          data={devicesQuery.data}
          isLoading={devicesQuery.isLoading}
          isError={devicesQuery.isError}
        />

        <FeatureUsagePanel
          data={featuresQuery.data}
          isLoading={featuresQuery.isLoading}
          isError={featuresQuery.isError}
        />

        <InteractionsPanel
          data={interactionsQuery.data}
          isLoading={interactionsQuery.isLoading}
          isError={interactionsQuery.isError}
        />

        <div className="mt-4 flex flex-wrap gap-x-8 gap-y-2 text-xs leading-relaxed text-gray-500">
          {!showPlatformView ? (
          <p className="max-w-2xl">
            Adoption is estimated from activity in {method?.bucket_minutes || 5}-minute
            slots, with {method?.grace_buckets ?? 1} slot
            {(method?.grace_buckets ?? 1) === 1 ? '' : 's'} of grace after each run of
            work — roughly how long the product was in use, not exact hours worked.
            Background polling is excluded, so an open tab alone counts for nothing.
            {isSingleDay
              ? ' Select a row to see the slots behind a number and how they signed in.'
              : ' Select a row to see how they signed in.'}
          </p>
          ) : null}
          {method ? (
            <p>
              Daily email:{' '}
              <span
                className={
                  method.report_enabled ? 'text-[#1c5f4c]' : 'text-orange-700'
                }>
                {method.report_enabled ? 'on' : 'off'}
              </span>
              {method.report_schedule ? ` · ${method.report_schedule} IST` : ''}
              {method.report_recipients?.length
                ? ` · to ${method.report_recipients.join(', ')}`
                : ''}
            </p>
          ) : null}
        </div>
      </div>

      {drillUser ? (
        <UserDetailModal
          user={drillUser}
          range={range}
          onClose={() => setDrillUser(null)}
        />
      ) : null}
    </Layout>
  );
};

export default TimeOnSystem;
